import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';
import './Employees.css';

const STATUSES = ['Active', 'On Leave', 'Resigned', 'Terminated', 'Retired', 'Absconded'];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const badgeClass = (status) => {
  if (status === 'Active') return 'badge-approved';
  if (status === 'On Leave') return 'badge-pending';
  return 'badge-rejected';
};

export default function Employees() {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ status: '', department: '', location: '', search: '' });
  const [opts, setOpts] = useState({ departments: [], locations: [] });
  const [selected, setSelected] = useState(new Set());
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);

  const statusFilterOptions = useMemo(
    () => [{ value: '', label: 'All statuses' }, ...STATUSES.map(s => ({ value: s, label: s }))],
    []
  );
  const departmentFilterOptions = useMemo(
    () => [{ value: '', label: 'All departments' }, ...opts.departments.map(d => ({ value: d, label: d }))],
    [opts.departments]
  );
  const locationFilterOptions = useMemo(
    () => [{ value: '', label: 'All locations' }, ...opts.locations.map(l => ({ value: l, label: l }))],
    [opts.locations]
  );

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) qs.set(k, v);
    });
    api
      .get(`/employees${qs.toString() ? `?${qs}` : ''}`)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get('/employees/_meta/options').then(setOpts).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(rows.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [rows.length, pageSize]);

  const totalFiltered = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = rows.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  const allVisibleSelected =
    pageRows.length > 0 && pageRows.every(r => selected.has(r.emp_id));

  const toggleAllVisible = () => {
    const pageIds = pageRows.map(r => r.emp_id);
    const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
    if (allPageSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} employee(s)? This cannot be undone.`)) return;
    try {
      const res = await api.post('/employees/bulk-delete', { ids: Array.from(selected) });
      setMsg(`Deleted ${res.deleted} employee(s).`);
      setSelected(new Set());
      load();
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    }
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm(`Delete ${id}?`)) return;
    try {
      await api.del(`/employees/${id}`);
      load();
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length, active: rows.filter(r => r.status === 'Active').length };
    STATUSES.forEach(s => {
      c[s] = rows.filter(r => r.status === s).length;
    });
    return c;
  }, [rows]);

  const hasFilters = !!(filters.search || filters.status || filters.department || filters.location);

  const clearFilters = () => {
    setFilters({ status: '', department: '', location: '', search: '' });
  };

  const msgIsError = msg.startsWith('Failed');

  return (
    <>
      <div className="page-header">
        <h1>Employee Master</h1>
      </div>
      <div className="page-content">
        <div className="employees-top">
          <div className="employees-stats" aria-label="Employee counts">
            <div className="employees-stat">
              <div className="employees-stat-label">In list</div>
              <div className="employees-stat-value">{loading ? '—' : counts.total.toLocaleString()}</div>
            </div>
            <div className="employees-stat">
              <div className="employees-stat-label">Active</div>
              <div className="employees-stat-value">{loading ? '—' : counts.active.toLocaleString()}</div>
            </div>
            <div className="employees-stat">
              <div className="employees-stat-label">Selected</div>
              <div className="employees-stat-value">{selected.size.toLocaleString()}</div>
            </div>
          </div>
          <div className="employees-top-right">
            {hasFilters && (
              <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            {canCreate('employees') && (
              <Link to="/employees/new" className="btn btn-primary btn-sm employees-new-btn">
                + New Employee
              </Link>
            )}
          </div>
        </div>

        <div className="employees-toolbar">
          <div className="employees-toolbar-left">
            <div className="employees-search">
              <span className="employees-search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                placeholder="Name, ID, email, designation…"
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                aria-label="Search employees"
              />
            </div>
            <div className="employees-filters">
              <ToolbarFilterDropdown
                id="emp-filter-status"
                labelText="Status"
                ariaLabel="Filter by status"
                value={filters.status}
                onChange={v => setFilters(f => ({ ...f, status: v }))}
                placeholder="All statuses"
                options={statusFilterOptions}
                menuKey="status"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
              <ToolbarFilterDropdown
                id="emp-filter-department"
                labelText="Department"
                ariaLabel="Filter by department"
                value={filters.department}
                onChange={v => setFilters(f => ({ ...f, department: v }))}
                placeholder="All departments"
                options={departmentFilterOptions}
                menuKey="department"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
                wide
              />
              <ToolbarFilterDropdown
                id="emp-filter-location"
                labelText="Location"
                ariaLabel="Filter by location"
                value={filters.location}
                onChange={v => setFilters(f => ({ ...f, location: v }))}
                placeholder="All locations"
                options={locationFilterOptions}
                menuKey="location"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
            </div>
          </div>
        </div>

        {msg && (
          <div className={`employees-banner${msgIsError ? ' error' : ''}`} role="status">
            {msg}
          </div>
        )}

        <div className="card">
          <div className="employees-table-head">
            <div className="employees-table-meta">
              <strong>{rows.length.toLocaleString()}</strong> in view
              {selected.size > 0 && (
                <>
                  {' · '}
                  <strong>{selected.size}</strong> selected
                </>
              )}
            </div>
            {canDelete('employees') && selected.size > 0 && (
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-sm btn-danger employees-icon-btn"
                  onClick={handleBulkDelete}
                  title={`Delete ${selected.size} selected`}
                  aria-label={`Delete ${selected.size} selected employees`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path
                      d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="employees-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      title="Select all rows on this page"
                      aria-label="Select all rows on this page"
                    />
                  </th>
                  <th>Emp ID</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Joining</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => (
                  <tr key={r.emp_id} className={selected.has(r.emp_id) ? 'employees-row-selected' : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(r.emp_id)}
                        onChange={() => toggleOne(r.emp_id)}
                        aria-label={`Select ${r.emp_id}`}
                      />
                    </td>
                    <td>
                      <Link to={`/employees/${r.emp_id}`} className="link">
                        {r.emp_id}
                      </Link>
                    </td>
                    <td className="employees-cell-name">
                      <div className="name-main">{r.full_name}</div>
                      <div className="name-sub">{r.email}</div>
                    </td>
                    <td>{r.designation}</td>
                    <td>{r.department}</td>
                    <td>{r.location}</td>
                    <td>
                      <span className={`badge ${badgeClass(r.status)}`}>{r.status}</span>
                    </td>
                    <td>{r.joining_date}</td>
                    <td>
                      <div className="employees-row-actions">
                        {canEdit('employees') && (
                          <Link
                            to={`/employees/${r.emp_id}`}
                            className="btn btn-sm btn-outline employees-icon-btn"
                            title="Edit employee"
                            aria-label={`Edit ${r.emp_id}`}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path
                                d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Link>
                        )}
                        {canDelete('employees') && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger employees-icon-btn"
                            onClick={() => handleDeleteOne(r.emp_id)}
                            title="Delete employee"
                            aria-label={`Delete ${r.emp_id}`}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path
                                d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinejoin="round"
                              />
                              <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && !loading && (
                  <tr>
                    <td colSpan={9} className="employees-empty">
                      No employees match the filters.
                    </td>
                  </tr>
                )}
                {!rows.length && loading && (
                  <tr>
                    <td colSpan={9} className="employees-empty">
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalFiltered > 0 && (
            <div className="customers-pagination" role="navigation" aria-label="Employee table pagination">
              <div className="customers-pagination-summary">
                <span className="customers-pagination-summary-label">Showing</span>
                <span className="customers-pagination-summary-range">
                  <strong className="tabular-nums">{rangeStart}</strong>
                  <span className="customers-pagination-summary-dash">–</span>
                  <strong className="tabular-nums">{rangeEnd}</strong>
                  <span className="customers-pagination-summary-of">of</span>
                  <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
                  <span className="customers-pagination-summary-unit">rows</span>
                </span>
              </div>
              <div className="customers-pagination-controls">
                <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
                  <span className="customers-pagination-label" id="employees-page-size-label">
                    Rows per page
                  </span>
                  <div className="customers-page-size-dd-inner">
                    <button
                      type="button"
                      className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
                      aria-haspopup="listbox"
                      aria-expanded={pageSizeOpen}
                      aria-label={`Rows per page, current value ${pageSize}`}
                      onClick={() => setPageSizeOpen(o => !o)}
                    >
                      <span>{pageSize}</span>
                      <span className="customers-page-size-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {pageSizeOpen && (
                      <ul
                        className="customers-page-size-menu"
                        role="listbox"
                        aria-labelledby="employees-page-size-label"
                      >
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <li
                            key={n}
                            role="option"
                            aria-selected={pageSize === n}
                            className={
                              pageSize === n
                                ? 'customers-page-size-option customers-page-size-option-active'
                                : 'customers-page-size-option'
                            }
                            onMouseDown={e => {
                              e.preventDefault();
                              setPageSize(n);
                              setPageSizeOpen(false);
                            }}
                          >
                            {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(1)}
                    aria-label="First page"
                    title="First page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 19l-7-7 7-7" />
                        <path d="M18 19l-7-7 7-7" />
                      </svg>
                    </span>
                    <span>First</span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </span>
                    <span>Prev</span>
                  </button>
                  <div className="customers-pagination-page-chip">
                    <span className="customers-pagination-page-label">Page</span>
                    <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
                    <span className="customers-pagination-page-sep">/</span>
                    <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span>Next</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(totalPages)}
                    aria-label="Last page"
                    title="Last page"
                  >
                    <span>Last</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 5l7 7-7 7" />
                        <path d="M6 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
